import { useEffect, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api } from "../lib/api";

type Field = {
  id: number;
  key: string;
  label: string;
  data_type: string;
  options: string[];
  is_segmentable: boolean;
  source: string;
  archived: boolean;
  in_use: number;
};

const TYPES = [
  { key: "text", hint: "anything — names, codes, notes" },
  { key: "number", hint: "counts and amounts you want to compare" },
  { key: "date", hint: "so you can ask 'in the last 30 days'" },
  { key: "boolean", hint: "yes or no" },
  { key: "enum", hint: "one of a fixed list" },
];

export function Fields() {
  const [fields, setFields] = useState<Field[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<number | null>(null);

  const load = () =>
    api.customFields().then(setFields).catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const create = () => {
    setBusy(true);
    api
      .createCustomField({
        label: label.trim(),
        data_type: type,
        options: type === "enum"
          ? options.split(",").map((o) => o.trim()).filter(Boolean)
          : undefined,
      })
      .then(() => {
        setLabel("");
        setOptions("");
        return load();
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const set = (id: number, patch: Record<string, unknown>) =>
    api.updateCustomField(id, patch).then(load).catch((e) => setError(e.message));

  const remove = (f: Field, purge: boolean) => {
    setConfirming(null);
    api
      .deleteCustomField(f.id, purge)
      .then(load)
      .catch((e) => setError(e.message));
  };

  const live = fields.filter((f) => !f.archived);
  const archived = fields.filter((f) => f.archived);

  return (
    <>
      <ErrorBanner error={error} />
      <h2 style={{ fontSize: 19 }}>Contact fields</h2>
      <p className="dim" style={{ marginTop: -4, fontSize: 13, maxWidth: 760 }}>
        Anything you want to know about a contact that isn't already built in. Imports
        create these automatically from columns we don't recognise, so most of them
        appear on their own — this is where you name them properly, decide their type,
        or get rid of them.
      </p>

      <Card title="Add a field">
        <div className="row w" style={{ gap: 8 }}>
          <input
            className="f"
            style={{ flex: "1 1 200px", minWidth: 0 }}
            placeholder="What is it called? e.g. Cabin class"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <select className="f" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.key} — {t.hint}
              </option>
            ))}
          </select>
          {type === "enum" && (
            <input
              className="f"
              style={{ flex: "1 1 200px", minWidth: 0 }}
              placeholder="economy, premium, business"
              value={options}
              onChange={(e) => setOptions(e.target.value)}
            />
          )}
          <button className="btn p" disabled={!label.trim() || busy} onClick={create}>
            {busy ? "…" : "Add"}
          </button>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
          The type decides how you can filter on it. <b>number</b> lets you ask "more
          than 3"; <b>date</b> lets you ask "in the last 30 days"; <b>text</b> only
          matches exactly. Getting it right here is what makes the audience builder
          useful later.
        </div>
      </Card>

      <Card title={`In use · ${live.length}`}>
        {live.length === 0 ? (
          <div className="empty">
            No custom fields yet. Import a CSV with columns we don't recognise and they
            appear here on their own.
          </div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Stored as</th>
                  <th>Type</th>
                  <th>Contacts with a value</th>
                  <th>Filterable</th>
                  <th>Came from</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {live.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <input
                        className="f"
                        style={{ width: "100%", minWidth: 120 }}
                        defaultValue={f.label}
                        onBlur={(e) =>
                          e.target.value !== f.label && set(f.id, { label: e.target.value })
                        }
                      />
                    </td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      attr.{f.key}
                    </td>
                    <td>
                      <select
                        className="f"
                        style={{ fontSize: 11.5 }}
                        value={f.data_type}
                        onChange={(e) => set(f.id, { data_type: e.target.value })}
                      >
                        {TYPES.map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.key}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="mono">{fmt.n(f.in_use)}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={f.is_segmentable}
                        onChange={(e) => set(f.id, { is_segmentable: e.target.checked })}
                      />
                    </td>
                    <td>
                      <Pill tone={f.source === "import" ? "nu" : "ac"}>{f.source}</Pill>
                    </td>
                    <td>
                      {confirming === f.id ? (
                        <div className="col" style={{ gap: 4 }}>
                          <button className="btn s" onClick={() => remove(f, false)}>
                            Hide it, keep the data
                          </button>
                          <button
                            className="btn s"
                            style={{ color: "var(--bad)", borderColor: "var(--bad)" }}
                            onClick={() => remove(f, true)}
                          >
                            Erase from {fmt.n(f.in_use)} contacts
                          </button>
                          <button className="btn s" onClick={() => setConfirming(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="x" onClick={() => setConfirming(f.id)}>
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {archived.length > 0 && (
        <Card title={`Hidden · ${archived.length}`}>
          <div className="dim" style={{ fontSize: 12, marginBottom: 10 }}>
            Not offered in the audience builder any more, but every value is still on
            the contacts. Adding a field with the same name brings it back.
          </div>
          {archived.map((f) => (
            <div className="brk" key={f.id}>
              <span>
                {f.label} <span className="dim mono" style={{ fontSize: 11 }}>attr.{f.key}</span>
              </span>
              <span className="row" style={{ gap: 8 }}>
                <span className="dim mono">{fmt.n(f.in_use)} still have a value</span>
                <button className="btn s" onClick={() => set(f.id, { archived: false })}>
                  Bring back
                </button>
              </span>
            </div>
          ))}
        </Card>
      )}

      <Card title="Where a contact's information lives">
        <div className="col" style={{ gap: 10, fontSize: 12.5 }}>
          <div>
            <b>Built-in fields</b> — <span className="dim">
              name, phone, state, consent, call counts, reply history, lead score,
              appointments, lifetime value. About 45 of them. The engine branches on
              these, so they are real columns and cannot be added to from here.
            </span>
          </div>
          <div>
            <b>Custom fields</b> — <span className="dim">
              this page. Unlimited, added at any time, no migration and no downtime.
              Filterable in the audience builder the moment they exist.
            </span>
          </div>
          <div>
            <b>Tags</b> — <span className="dim">
              yes/no labels with no value attached, applied by imports, replies, or by
              hand. Use a tag for "is a VIP", a field for "cabin class".
            </span>
          </div>
        </div>
      </Card>
    </>
  );
}
